import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { BACKUP_STATE, fmtParis } from "@/lib/constants";
import { TONES } from "./parts";

const BACKUP_TONE = { success: "green", running: "cyan", unknown: "grey", test_only: "grey" };

const Stat = ({ to, label, value, tone = "grey", testid }) => (
  <Link to={to} data-testid={testid} className="flex items-center justify-between gap-3 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 transition-colors hover:bg-white/[0.06]">
    <span className="text-sm text-slate-300">{label}</span>
    <span className={`font-display text-lg font-semibold tabular-nums ${value ? TONES[tone].text : "text-slate-500"}`}>{value ?? "—"}</span>
  </Link>
);

export const BackupDot = ({ backup }) => {
  const tone = TONES[BACKUP_TONE[backup.state] || "red"];
  const when = backup.last_report?.started_at;
  return (
    <Link to="/sauvegardes" className="flex items-center gap-2 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 text-sm hover:bg-white/[0.06]" data-testid="kpi-backup">
      <span className={`h-2.5 w-2.5 rounded-full ${tone.dot}`} />
      <span className="text-slate-300">Sauvegarde</span>
      <span className={`ml-auto text-right ${tone.text}`} data-testid="kpi-backup-state">
        {BACKUP_STATE[backup.state] || backup.state}{when && <span className="block text-xs text-slate-500">Dernier rapport : {fmtParis(when)}</span>}
      </span>
    </Link>
  );
};

export const SecondaryZone = ({ c, s, v3, backup }) => {
  const [open, setOpen] = useState(() => localStorage.getItem("dt-dash-more") === "1");
  const toggle = () => setOpen((v) => { localStorage.setItem("dt-dash-more", v ? "0" : "1"); return !v; });
  const dot = backup ? TONES[BACKUP_TONE[backup.state] || "red"].dot : null;
  return (
    <section className="rounded-2xl border border-white/[0.07] bg-[#0F1F3D]/60" data-testid="zone-secondary">
      <button onClick={toggle} className="flex w-full items-center gap-3 px-4 py-4 text-left sm:px-5" data-testid="secondary-toggle" aria-expanded={open}>
        <span className="font-display text-base font-semibold text-white">Autres indicateurs</span>
        {dot && <span className={`h-2 w-2 rounded-full ${dot}`} title="État des sauvegardes" />}
        <ChevronDown className={`ml-auto h-5 w-5 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="grid gap-2 px-4 pb-4 sm:grid-cols-2 sm:px-5 lg:grid-cols-3" data-testid="secondary-content">
          <Stat to="/interventions?cat=done" label="Interventions terminées" value={c.done} tone="green" testid="kpi-done" />
          <Stat to="/interventions?cat=drafts" label="Brouillons" value={c.drafts} tone="orange" testid="kpi-drafts" />
          <Stat to="/rappels" label="Rappels sur 7 jours" value={s.reminders_soon} tone="cyan" testid="kpi-reminders-soon" />
          {v3 && <Stat to="/contrats?status=actif" label="Contrats actifs" value={v3.contracts_active} tone="green" testid="kpi-contracts-active" />}
          {v3 && <Stat to="/contrats?status=expiring" label="Contrats à échéance" value={v3.contracts_expiring} tone="orange" testid="kpi-contracts-expiring" />}
          {v3 && <Stat to="/contrats?status=preventive_due" label="Préventif à programmer" value={v3.preventive_due} tone="cyan" testid="kpi-preventive-due" />}
          {v3 && <Stat to="/contrats?status=low_credit" label="Crédit d'heures faible" value={v3.low_credit} tone="orange" testid="kpi-low-credit" />}
          <Stat to="/clients" label="Clients" value={s.clients} tone="cyan" testid="kpi-clients" />
          {backup && <BackupDot backup={backup} />}
        </div>
      )}
    </section>
  );
};
